import { z } from "zod";
import { findProvince, provincesOf } from "../utils/provinces";

// Messages are translation keys (namespace `return-widget-v3`) that the screens
// pass through `useCustomText`. They are not in `widget-keys.generated.ts`: the
// scanner only picks up literal `t("…")` calls, so the office's portal wording
// screen does not offer them for override.
export const addressSchema = z
  .object({
    companyName: z.string().optional().default(""),
    first_name: z.string().min(1, "address.error.firstNameRequired"),
    last_name: z.string().min(1, "address.error.lastNameRequired"),
    address1: z
      .string()
      .min(1, "address.error.address1Required")
      .max(230, "address.error.address1TooLong"),
    address2: z
      .string()
      .max(230, "address.error.address2TooLong")
      .optional()
      .default(""),
    city: z.string().min(1, "address.error.cityRequired"),
    zip: z
      .string()
      .min(1, "address.error.zipRequired")
      .transform((v) => v.trim()),
    // Alpha-2 only: carrier compatibility matches country codes by strict string
    // equality against ISO 3166-1 alpha-2 lists, so a country name or an alpha-3
    // code resolves no carrier at all and the return ends up without a label.
    country_code: z
      .string()
      .min(1, "address.error.countryRequired")
      .regex(/^[A-Za-z]{2}$/, "address.error.countryInvalid"),
    email: z.string().email("address.error.emailInvalid"),
    phone: z.string().min(1, "address.error.phoneRequired"),
    // Only asked where `provincesOf` has a list (US, CA, AU): there the carriers
    // refuse the label without it, so it is required and must be one of the codes.
    province_code: z.string().optional().default(""),
  })
  .superRefine((values, ctx) => {
    if (!provincesOf(values.country_code)) return;
    if (!findProvince(values.country_code, values.province_code)) {
      ctx.addIssue({
        code: "custom",
        path: ["province_code"],
        message: "address.error.provinceRequired",
      });
    }
  });

export type AddressFormValues = z.infer<typeof addressSchema>;

type ProvinceSource = {
  country_code?: string | null;
  province?: string | null;
  province_code?: string | null;
};

/**
 * Outside the countries with a state field, the form has nothing to say about
 * the state, so the order's one is carried over — otherwise the return, and the
 * exchange order built from its address, were saved without it.
 *
 * The state is only carried over while the customer keeps the order's country:
 * one picked for another country would be wrong rather than missing.
 */
export function withOrderProvince<T extends { country_code: string }>(
  values: T,
  orderAddress: ProvinceSource | null | undefined,
): T & { province: string | null; province_code: string | null } {
  const sameCountry =
    !!orderAddress?.country_code &&
    orderAddress.country_code.toUpperCase() ===
      values.country_code.toUpperCase();

  return {
    ...values,
    province: sameCountry ? (orderAddress.province ?? null) : null,
    province_code: sameCountry ? (orderAddress.province_code ?? null) : null,
  };
}

/**
 * The state the return is saved with: the one picked in the form where there is
 * a field, the order's one elsewhere (`withOrderProvince`).
 */
export function withProvince<
  T extends { country_code: string; province_code?: string | null },
>(
  values: T,
  orderAddress: ProvinceSource | null | undefined,
): T & { province: string | null; province_code: string | null } {
  if (!provincesOf(values.country_code)) {
    return withOrderProvince(values, orderAddress);
  }
  const picked = findProvince(values.country_code, values.province_code);
  return {
    ...values,
    province: picked?.name ?? null,
    province_code: picked?.code ?? null,
  };
}

/** Prefills the state field with the order's state when the form shows one. */
export function defaultProvinceCode(
  address: ProvinceSource | null | undefined,
): string {
  return (
    findProvince(address?.country_code, address?.province_code)?.code ?? ""
  );
}
